import SelectFormComponent from "components/shared/FormComponent/SelectFormComponent";
import StyledButton from "components/shared/StyledButton";
import { IChannel } from "containers/Channels/types";
import { transferWorkLabel } from "containers/TransferTermFormContainer/utils";
import { formatNumber } from "helpers/utils";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store/store";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { convertTransferTermSale } from "store/transferTermSalesStore";
import { ChannelKind } from "store/types";

import styles from "./style.module.scss";

interface ITransferRightsModal {
    /**
     * Payment from which transfer is done.
     */
    termSale: ITransferTermSale | undefined;
    handleClose: () => void;
}

/**
 * Modal accessed from TransferTermSalesTable. Transfers rights to Management sale.
 */
const TransferRightsModal = ({ termSale, handleClose }: ITransferRightsModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { channels } = useAppSelector((state) => state.channels);
    const rightsTransferChannels = channels.filter((channel) => channel.kind === ChannelKind.RIGHTS_TRANSFER);

    // Rights-transfer channels are selected by kind; post-revamp they no longer carry channel.type.
    const channelOptions = useMemo(
        () => rightsTransferChannels.map((channel) => ({ value: channel.id, label: channel.name })),
        [rightsTransferChannels],
    );

    const defaultValues = {
        channelId: channelOptions.length ? channelOptions[0].value : null,
    };

    const { control, handleSubmit } = useForm({ defaultValues, mode: "all" });

    const onSubmit = async (data: { channelId: number | null }) => {
        if (termSale && data.channelId != null) {
            const response = await dispatch(
                convertTransferTermSale({ saleId: termSale?.id, channelId: data.channelId }),
            );
            if (response.meta.requestStatus) {
                handleClose();
            }
        }
    };

    return (
        <form className={styles["transfer-rights-form"]} onSubmit={handleSubmit(onSubmit)}>
            {!!channelOptions.length && termSale ? (
                <>
                    <h3>
                        {t("TransferTerms.PaymentModal.TransferInfo1")}
                        <span>{formatNumber(termSale?.netAmount / 100, { isCurrency: true })}</span>
                        {t("TransferTerms.PaymentModal.TransferInfo2")}
                        <span>{transferWorkLabel(termSale ?? {})}.</span>
                    </h3>
                    <SelectFormComponent
                        control={control}
                        controlName="channelId"
                        label={t("TransferTerms.PaymentModal.ChooseChannel")}
                        items={channelOptions}
                    />
                </>
            ) : (
                <span className={styles["no-channel-info"]}>{t("TransferTerms.PaymentModal.NoChannel")}</span>
            )}

            <StyledButton buttonType="primary" type="submit">
                {t("TransferTerms.Modals.TransferRights")}
            </StyledButton>
        </form>
    );
};

export default TransferRightsModal;
